import React from "react";

import { injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import DrinkAlcoholic from "@/icons/__generated__/DrinkAlcoholic/DrinkAlcoholic24";
import { IFaqs } from "~/types/api-types";
import {
  IAlcoholConsumptionOptionsMessagesOptions,
  IBringAlcoholMessagesOptions,
  IBringAlcoholRulesOptions,
} from "~/types/local-types";

import {
  eventFaqDefinition,
  category_labels,
} from "../data/event-faq-definitions";
import { alcoholRules } from "../data/event-faq-display-sections";

import Description from "./Description";
import { analyticEventOnClick } from "./faq-utils";

interface AlcoholProps {
  faqData: IFaqs;
  intl: IntlShape;
  userID: string;
  listingID: string;
}

const Alcohol: React.FC<AlcoholProps> = ({
  faqData,
  intl,
  userID,
  listingID,
}) => {
  const { alcoholRule, moreInformation, crossedOutRules } = alcoholRules(
    faqData,
  ) as {
    alcoholRule: keyof IBringAlcoholMessagesOptions;
    moreInformation: keyof IBringAlcoholRulesOptions;
    crossedOutRules: (keyof IAlcoholConsumptionOptionsMessagesOptions)[];
  };

  const hasMoreInformationRules =
    Array.isArray(moreInformation) && moreInformation.length > 0;

  const hasCrossedOutRules = crossedOutRules.length > 0;

  const moreAlcoholRules =
    !!faqData["other_alcohol_consumption_rules"] ||
    hasMoreInformationRules ||
    hasCrossedOutRules;

  const label = intl.formatMessage(category_labels.alcohol);

  const alcoholMessage = eventFaqDefinition.alcohol_beverages?.bring_alcohol[
    alcoholRule
  ] ?? {
    id: "",
    defaultMessage: "",
  };

  const moreInformationMessage = eventFaqDefinition.alcohol_beverages
    ?.bring_alcohol_rules[moreInformation] ?? {
    id: "",
    defaultMessage: "",
  };

  const findRule = (rule: string) => {
    return (
      eventFaqDefinition.alcohol_beverages?.alcohol_consumption_options[
        rule as keyof IAlcoholConsumptionOptionsMessagesOptions
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  const findCrossedOutRules = (rule: string) => {
    return (
      eventFaqDefinition.alcohol_beverages?.alcohol_consumption_options[
        rule as keyof IAlcoholConsumptionOptionsMessagesOptions
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  const otherConsumptionRules = eventFaqDefinition.alcohol_beverages
    ?.alcohol_consumption_options["other_alcohol_consumption_rules"] ?? {
    id: "",
    defaultMessage: "",
  };

  return (
    <div className="EventFAQ">
      <Accordion
        type="multiple"
        onValueChange={() => {
          analyticEventOnClick(userID, listingID, "Alcohol");
        }}
      >
        <AccordionItem value="alcohol-section">
          <AccordionTrigger className="tw-py-24">
            <div className="tw-flex tw-gap-12 tw-items-center">
              <DrinkAlcoholic />
              <Text element="h3" typeface="headline-1-m">
                {label}
              </Text>
            </div>
          </AccordionTrigger>
          <AccordionContent className="tw-ml-36 tw-pb-24 tw-space-y-8">
            <Text typeface="body-2-m" element="p">
              {intl.formatMessage(alcoholMessage)}
            </Text>
            {"bring_alcohol_allowed" === alcoholRule && (
              <Text element="p">
                {intl.formatMessage(moreInformationMessage)}
              </Text>
            )}
            {moreAlcoholRules && (
              <>
                {hasMoreInformationRules &&
                  moreInformation.map((rule, key) => (
                    <Text element="p" key={key}>
                      {intl.formatMessage(findRule(rule))}
                    </Text>
                  ))}
                {faqData["other_alcohol_consumption_rules"] &&
                  "bring_alcohol_not_allowed" === alcoholRule && (
                    <div>
                      <Text element="p">
                        {intl.formatMessage(otherConsumptionRules)}
                      </Text>
                      <Description
                        description={faqData["other_alcohol_consumption_rules"]}
                        textSmall
                      />
                    </div>
                  )}
                {hasCrossedOutRules &&
                  crossedOutRules.map((rule, key) => (
                    <Text
                      className="tw-text-gray-500 tw-line-through"
                      key={key}
                    >
                      {intl.formatMessage(findCrossedOutRules(rule))}
                    </Text>
                  ))}
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default injectIntl(Alcohol);
